import type { UseMutateFunction, MutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { LoadingButton } from 'shared/ui/loading-button';
import { useIsProjectControlButtonDisabled } from '@/enteties/project/hooks/useIsProjectControlButtonDisabled';
import type { ProjectControlPropsWithTimeout } from 'features/build-project/ui/build-project';
import type { StatusResponse } from 'shared/api/services/types';
import { useIsRerunDisabled } from '@/enteties/project/hooks/useIsRerunDisabled';
interface RerunProjectProps<TData extends StatusResponse> extends ProjectControlPropsWithTimeout {
  mutationOptions: MutationOptions<TData, Error, void>;
  preRerun?: (restartProject: UseMutateFunction) => void;
  toolTipContent?: string;
}
export const RerunProject = <TData extends StatusResponse,>({
  slug,
  project,
  isTimedOut,
  setIsTimedOut,
  buttonChild,
  mutationOptions,
  preRerun,
  toolTipContent,
  ...props
}: RerunProjectProps<TData>) => {
  const {
    mutate: restartProject,
    isPending
  } = useMutation({
    ...mutationOptions,
    onSuccess: () => {
      if (setIsTimedOut) {
        setTimeout(() => setIsTimedOut(false), 15_000);
      }
    }
  });
  const isRerunDisabled = useIsRerunDisabled(project);
  const isDefaultDisabled = useIsProjectControlButtonDisabled(project, slug, true);
  const isDisabled = useMemo(() => isTimedOut || isDefaultDisabled || isRerunDisabled, [isDefaultDisabled, isRerunDisabled, isTimedOut]);
  const handleOnClick = useCallback(() => {
    if (setIsTimedOut) {
      setIsTimedOut(true);
    }
    if (preRerun) {
      preRerun(restartProject);
    } else {
      restartProject();
    }
  }, [restartProject]);
  return <LoadingButton loading={isPending} disabled={isDisabled} size="icon-highlight" variant="highlighted" onClick={handleOnClick} className="w-full" loaderSize="w-5 h-5" toolTipContent={toolTipContent} {...props} data-sentry-element="LoadingButton" data-sentry-component="RerunProject" data-sentry-source-file="rerun-project.tsx">
      {buttonChild}
    </LoadingButton>;
};